// The signup-nudge dialog: the single conversion surface shown when a
// visitor or anonymous user tries to generate. Two variants:
//   - 'landing': dismiss = "Continue without account" → guest login via
//     browser fingerprint, then straight to /minimax-h3 (the caller has
//     already stashed the draft via lib/landingDraft).
//   - 'inApp': dismiss = plain close; shown to anonymous users when they hit
//     Generate anywhere in the app.
// Mounted once by SignupNudgeProvider (inside Layout and on the Landing
// page); pages reach it through useSignupNudge().

import { createContext, useContext, useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/CloseRounded';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcardRounded';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailReadRounded';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { format, useTranslation } from '../i18n/I18nProvider';
import { PROMO_CREDITS } from '../lib/promo';
import { getFingerprintNow } from '../hooks/useFingerprint';

type Variant = 'landing' | 'inApp';

type SignupNudgeContextValue = {
  // Open the dialog explicitly (banners, feature cards, landing Generate).
  open: (variant?: Variant) => void;
  // Pre-submit gate for generation pages: opens the dialog and returns true
  // when the current user is anonymous (the submit must be aborted).
  gateGenerate: () => boolean;
};

const SignupNudgeContext = createContext<SignupNudgeContextValue | null>(null);

export function useSignupNudge(): SignupNudgeContextValue {
  const ctx = useContext(SignupNudgeContext);
  if (!ctx) throw new Error('useSignupNudge must be used within SignupNudgeProvider');
  return ctx;
}

export function SignupNudgeProvider({ children }: { children: ReactNode }) {
  const { user, signIn, signUp, signInAnonymously } = useAuth();
  const t = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [variant, setVariant] = useState<Variant>('inApp');
  const [guestBusy, setGuestBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = (v: Variant = 'inApp') => {
    setVariant(v);
    setError(null);
    setDialogOpen(true);
  };

  const gateGenerate = () => {
    if (!user?.isAnonymous) return false;
    open('inApp');
    return true;
  };

  // Where Casdoor should drop the user after auth. From the landing page the
  // draft is waiting on /minimax-h3; in-app, come back to the current page.
  const returnTo =
    variant === 'landing' ? '/minimax-h3' : location.pathname + location.search;

  const close = () => {
    if (guestBusy) return;
    setDialogOpen(false);
  };

  const continueAsGuest = async () => {
    setGuestBusy(true);
    setError(null);
    try {
      const fingerprint = await getFingerprintNow();
      await signInAnonymously(fingerprint);
      setDialogOpen(false);
      navigate('/minimax-h3');
    } catch (err) {
      console.warn('guest sign-in failed:', err);
      setError(t.signupNudge.guestError);
    } finally {
      setGuestBusy(false);
    }
  };

  return (
    <SignupNudgeContext.Provider value={{ open, gateGenerate }}>
      {children}
      <Dialog open={dialogOpen} onClose={close} maxWidth="xs" fullWidth>
        <DialogContent sx={{ p: 4, textAlign: 'center' }}>
          <IconButton
            aria-label={t.common.close}
            onClick={close}
            size="small"
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
            {t.signupNudge.title}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            {t.signupNudge.subtitle}
          </Typography>

          <Stack spacing={1.5} sx={{ mb: 3, textAlign: 'left' }}>
            <Benefit
              icon={<CardGiftcardIcon color="primary" />}
              text={format(t.signupNudge.benefitSignup, { credits: PROMO_CREDITS.SIGNUP })}
            />
            <Benefit
              icon={<MarkEmailReadIcon color="primary" />}
              text={format(t.signupNudge.benefitEmail, { credits: PROMO_CREDITS.EMAIL_VERIFY })}
            />
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
              {error}
            </Alert>
          )}

          <Stack spacing={1.5}>
            <Button
              variant="contained"
              size="large"
              onClick={() => signUp(returnTo)}
              disabled={guestBusy}
            >
              {t.signupNudge.signUp}
            </Button>
            <Button variant="outlined" onClick={() => signIn(returnTo)} disabled={guestBusy}>
              {t.signupNudge.signIn}
            </Button>
            {variant === 'landing' && (
              <Button
                variant="text"
                size="small"
                onClick={continueAsGuest}
                disabled={guestBusy}
                startIcon={guestBusy ? <CircularProgress size={14} /> : undefined}
                sx={{ color: 'text.secondary' }}
              >
                {guestBusy ? t.signupNudge.guestWorking : t.signupNudge.continueWithout}
              </Button>
            )}
          </Stack>
        </DialogContent>
      </Dialog>
    </SignupNudgeContext.Provider>
  );
}

function Benefit({ icon, text }: { icon: ReactNode; text: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      {icon}
      <Typography variant="body2">{text}</Typography>
    </Box>
  );
}
